import type { FC } from 'react'
import { useState } from 'react'

import { LoaderCircle } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Form, Formik } from 'formik'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import adminQueries from '@/modules/admin/queries'
import type { ReportFilters, ReportSetupExportSettings } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'

import type { ExportOptionsType } from '../resources/reports/types'

import { getReportExportUsingAPI } from './exportFormats'
import { AccountingOptions } from './ExportOptions/AccountingOptions'
import { ConsumptionOptions } from './ExportOptions/ConsumptionOptions'
import { NewNumberOrdersOptions } from './ExportOptions/NewNumberOrdersOptions'
import { OffBoardingOptions } from './ExportOptions/OffBoardingOptions'
import { OnOffboardingOptions } from './ExportOptions/OnOffboardingOptions'
import { SalaryDeductionDeviceOptions } from './ExportOptions/SalaryDeductionDeviceOptions'
import { SalaryDeductionUsageOptions } from './ExportOptions/SalaryDeductionUsageOptions'
import { TaxAdvantageOptions } from './ExportOptions/TaxAdvantageOptions'
import { SddHuldtOgLillevik } from './ExportSettingsFormInputs/SalaryDeductionDevice/HuldtOgLillevik'
import { SddTripletex } from './ExportSettingsFormInputs/SalaryDeductionDevice/Tripletex'
import { SddVismaLonnV3 } from './ExportSettingsFormInputs/SalaryDeductionDevice/VismaLonnV3'
import { SduHuldtOgLillevik } from './ExportSettingsFormInputs/SalaryDeductionUsage/HuldtOgLillevik'
import { SduTripletex } from './ExportSettingsFormInputs/SalaryDeductionUsage/Tripletex'
import { SduVismaLonnV3 } from './ExportSettingsFormInputs/SalaryDeductionUsage/VismaLonnV3'
import { capitalizeStr } from './utils'

type Step = 'EXPORT_TYPES' | 'EXPORT_OPTIONS'

export const ExportSettings: FC<{
  filters: ReportFilters
  onClose: () => void
}> = ({ filters, onClose }) => {
  const translate = useTranslation()
  const [step, setStep] = useState<Step>('EXPORT_TYPES')
  const [selectedOption, setSelectedOption] = useState<ExportOptionsType | null>()
  const { selectedReportType } = filters

  const isSduVismaLonnV3 =
    selectedReportType === 'salary-deduction-usage' && selectedOption === 'visma_lonn_v3'
  const isSduHuldtOgLillevik =
    selectedReportType === 'salary-deduction-usage' && selectedOption === 'huldt_og_lillevik'
  const isSddVismaLonnV3 = selectedReportType === 'ssd-order' && selectedOption === 'visma_lonn_v3'
  const isSddHuldtOgLillevik =
    selectedReportType === 'ssd-order' && selectedOption === 'huldt_og_lillevik'
  const isSduTripletex =
    selectedReportType === 'salary-deduction-usage' && selectedOption === 'tripletex'
  const isSddTripletex = selectedReportType === 'ssd-order' && selectedOption === 'tripletex'

  const [initialValues, setInitialValues] = useState<ReportSetupExportSettings>()

  const { isLoading: isExportSettingsLoading, refetch } = useQuery(
    adminQueries.adminSetupExportSettings(),
    adminResources.getSetupExportSettings,
    {
      onSuccess(data) {
        setInitialValues(data)
      },
      cacheTime: 0,
    }
  )

  const { mutateAsync, isLoading } = useMutation(adminResources.postSetupExportSettings, {
    onSuccess() {
      onClose()
      if (!selectedOption) return
      switch (selectedReportType) {
        case 'salary-deduction-usage':
          getReportExportUsingAPI({
            reportName: 'salary-deduction-usage',
            formatName: selectedOption,
            fromDate: filters.fromDate,
            toDate: filters.toDate,
            isGlobal: filters.isGlobal,
          })
          break
        case 'ssd-order':
          getReportExportUsingAPI({
            reportName: 'ssd-order',
            formatName: selectedOption,
            fromDate: filters.fromDate,
            toDate: filters.toDate,
            isGlobal: filters.isGlobal,
          })
          break
      }
    },
  })

  const handleSubmit = async (values: ReportSetupExportSettings) => {
    if (!selectedOption) return
    await mutateAsync(values)
    // refetch again in order to get the values updated in the settings step
    refetch()
  }

  const selectedReportTypeTitle = capitalizeStr(selectedReportType)
  const selectedOptionTitle = capitalizeStr(selectedOption || '')

  const onExportOptionClick = (id: ExportOptionsType) => {
    setSelectedOption(id)
    setStep('EXPORT_OPTIONS')
  }

  const modalTitle = selectedReportTypeTitle
    ? selectedReportTypeTitle + (selectedOptionTitle ? ` - ${selectedOptionTitle}` : '')
    : translate('Download Report')

  if (step === 'EXPORT_TYPES') {
    return (
      <>
        <h2>
          <Translation id={modalTitle} />
        </h2>

        {selectedReportType === 'new-number-orders' && (
          <NewNumberOrdersOptions title={modalTitle} filters={filters} />
        )}

        {selectedReportType === 'accounting' && (
          <AccountingOptions
            filters={filters}
            title={modalTitle}
            reportSetupExportSettings={initialValues}
          />
        )}

        {selectedReportType === 'tax-advantage' && (
          <TaxAdvantageOptions filters={filters} title={modalTitle} />
        )}

        {selectedReportType === 'consumption' && (
          <ConsumptionOptions filters={filters} title={modalTitle} />
        )}

        {selectedReportType === 'salary-deduction-usage' && (
          <SalaryDeductionUsageOptions
            filters={filters}
            title={modalTitle}
            onExportOptionClick={onExportOptionClick}
            reportSetupExportSettings={initialValues}
          />
        )}

        {selectedReportType === 'ssd-order' && (
          <SalaryDeductionDeviceOptions
            filters={filters}
            title={modalTitle}
            onExportOptionClick={onExportOptionClick}
            reportSetupExportSettings={initialValues}
          />
        )}

        {selectedReportType === 'off-boarding' && (
          <OffBoardingOptions title={modalTitle} filters={filters} />
        )}

        {selectedReportType === 'onboarding' && (
          <OnOffboardingOptions title={modalTitle} filters={filters} type="onboarding" />
        )}

        {selectedReportType === 'offboarding' && (
          <OnOffboardingOptions title={modalTitle} filters={filters} type="offboarding" />
        )}

        <div className="flex justify-end">
          <Button onClick={onClose} disabled={isLoading}>
            <Translation id="Cancel" />
          </Button>
        </div>
      </>
    )
  }

  if (isExportSettingsLoading) {
    return (
      <div className="flex items-center justify-center">
        <LoaderCircle />
      </div>
    )
  }

  return (
    <>
      <h2>
        <Translation id={selectedOptionTitle} />
      </h2>
      <Formik
        enableReinitialize
        initialValues={{
          salaryDeductionCodeDevice: initialValues?.salaryDeductionCodeDevice ?? '',
          salaryDeductionCodeUsage: initialValues?.salaryDeductionCodeUsage ?? '',
          salaryDeductionCodeBuyout: initialValues?.salaryDeductionCodeBuyout ?? '',
          projectDevice: initialValues?.projectDevice ?? '',
          projectUsage: initialValues?.projectUsage ?? '',
          departmentOverrideDevice: initialValues?.departmentOverrideDevice ?? '',
          departmentOverrideUsage: initialValues?.departmentOverrideUsage ?? '',
        }}
        onSubmit={handleSubmit}
      >
        <Form>
          {isSduVismaLonnV3 && <SduVismaLonnV3 />}
          {isSduHuldtOgLillevik && <SduHuldtOgLillevik />}
          {isSddVismaLonnV3 && <SddVismaLonnV3 />}
          {isSddHuldtOgLillevik && <SddHuldtOgLillevik />}
          {isSddTripletex && <SddTripletex />}
          {isSduTripletex && <SduTripletex />}
          <footer className="flex justify-end">
            <Button onClick={() => setStep('EXPORT_TYPES')} disabled={isLoading}>
              <Translation id="Back" />
            </Button>
            <Button variant="primary" type="submit" isLoading={isLoading}>
              <Translation id="Save & Export" />
            </Button>
          </footer>
        </Form>
      </Formik>
    </>
  )
}
